import { useRef, type KeyboardEvent } from "react";

export type ClubDetailSection =
  | "about"
  | "location"
  | "contact"
  | "matches"
  | "events";

type TabItem = {
  id: ClubDetailSection;
  label: string;
  icon: string;
};

type Props = {
  activeSection: ClubDetailSection;
  ariaLabel: string;
  labels: Record<ClubDetailSection, string>;
  onChange: (section: ClubDetailSection) => void;
};

const TAB_ICONS: Record<ClubDetailSection, string> = {
  about: "",
  location: "",
  contact: "",
  matches: "",
  events: "",
};

export default function ClubDetailTabs({
  activeSection,
  ariaLabel,
  labels,
  onChange,
}: Props) {
  const buttonRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const tabs: TabItem[] = (Object.keys(TAB_ICONS) as ClubDetailSection[]).map(
    (id) => ({ id, label: labels[id], icon: TAB_ICONS[id] }),
  );

  function handleKeyDown(
    event: KeyboardEvent<HTMLButtonElement>,
    currentIndex: number,
  ) {
    let nextIndex = currentIndex;

    if (event.key === "ArrowRight")
      nextIndex = (currentIndex + 1) % tabs.length;
    if (event.key === "ArrowLeft") {
      nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
    }
    if (event.key === "Home") nextIndex = 0;
    if (event.key === "End") nextIndex = tabs.length - 1;
    if (nextIndex === currentIndex) return;

    event.preventDefault();
    onChange(tabs[nextIndex].id);
    buttonRefs.current[nextIndex]?.focus();
  }

  return (
    <nav className="club-detail__tabs-shell" aria-label={ariaLabel}>
      <div className="club-detail__tabs" role="tablist">
        {tabs.map((tab, index) => {
          const selected = activeSection === tab.id;
          return (
            <button
              ref={(node) => {
                buttonRefs.current[index] = node;
              }}
              key={tab.id}
              id={`club-detail-tab-${tab.id}`}
              className="club-detail__tab"
              type="button"
              role="tab"
              aria-controls={
                selected ? `club-detail-panel-${tab.id}` : undefined
              }
              aria-selected={selected}
              tabIndex={selected ? 0 : -1}
              onClick={() => onChange(tab.id)}
              onKeyDown={(event) => handleKeyDown(event, index)}
            >
              <span className="material-symbols-outlined" aria-hidden="true">
                {tab.icon}
              </span>
              <span>{tab.label}</span>
            </button>
          );
        })}
      </div>
    </nav>
  );
}
